import { useRef, useState } from 'react';
import { useSession } from 'next-auth/react';
import useToastContext from '@contexts/ToastContext/hook';
import { updateDashboardVersion } from '@api/profile';
import {
  DashboardVersion,
  resolveDashboardVersion,
} from '@interfaces/dashboardVersion';
import {
  getVisitDashboardVersion,
  setVisitDashboardVersion,
} from '@repository/dashboardVersion';

/**
 * O valor inicial sai da sessão — o callback `session` do NextAuth já relê `/profile` —, então a home não
 * desenha uma versão para trocar pela outra em seguida.
 *
 * A troca não espera o PATCH: o clique é a saída de emergência de quem precisa do número agora. Falha ao
 * salvar não desfaz a troca, só avisa que a próxima visita pode não lembrar.
 */
export const useDashboardVersion = () => {
  const session = useSession();
  const toast = useToastContext();

  const userId = session.data?.user?.id;

  const [version, setVersion] = useState<DashboardVersion>(
    () =>
      getVisitDashboardVersion(userId) ??
      resolveDashboardVersion(session.data?.user?.dashboardVersion),
  );

  const isSaving = useRef(false);
  const queued = useRef<DashboardVersion | null>(null);

  /**
   * Um PATCH de cada vez, e o último clique é a última requisição.
   *
   * Dois cliques antes da primeira resposta deixariam as duas em voo, e o backend guarda a que chegar por
   * último: a tela mostraria a versão nova e a próxima sessão abriria na anterior. Enfileirar em vez de
   * disparar em paralelo é o que garante a ordem, já que o vencedor aqui é quem escreve por último.
   */
  const save = async (target: DashboardVersion) => {
    isSaving.current = true;

    try {
      await updateDashboardVersion(target);
    } catch {
      toast.setContent(
        'Trocamos a versão agora, mas não foi possível salvar sua preferência. Ao abrir o dashboard de novo, ele pode voltar para a versão anterior à sua escolha.',
        'warning',
      );
    } finally {
      isSaving.current = false;
    }

    const next = queued.current;
    queued.current = null;

    if (next && next !== target) await save(next);
  };

  const chooseVersion = (chosen: DashboardVersion) => {
    if (chosen === version) return;

    setVisitDashboardVersion(userId, chosen);
    setVersion(chosen);

    if (isSaving.current) {
      queued.current = chosen;
      return;
    }

    void save(chosen);
  };

  return { version, chooseVersion };
};
